import React, { useEffect, useMemo, useState } from 'react';
import ListEditor from './components/ListEditor';
import ImageUploader from './components/ImageUploader';
import FormActions from './components/FormActions';
import './components/ListEditor.css';
import './ServiceForm.css';
import useConfirmNavigation from '../../hooks/useConfirmNavigation';

const defaultService = {
  name: '',
  alias: '',
  shortDescription: '',
  sortOrder: 0,
  heroLabel: 'SERVICES WE OFFER',
  heroTitle: '',
  heroDescription: '',
  heroCtaText: 'Get a free quote',
  heroMainImage: '',
  approachImage: '',
  processLabel: 'PROCESS',
  processTitle: '',
  toolsLabel: 'TOOLS WE USE',
  toolsTitle: '',
  mobileAppsLabel: 'MOBILE APP DEVELOPMENT',
  mobileAppsTitle: '',
  portfolioLabel: 'WORK',
  portfolioTitle: '',
  approachList: [],
  toolsList: [],
  mobileApps: [],
  faqs: [],
  service_icon: '',
  featuredPortfolioIds: []
};

const buildFormState = (data = {}) => {
  const safeData = data && typeof data === 'object' ? data : {};
  return {
    ...defaultService,
    ...safeData,
    shortDescription: safeData.shortDescription ?? defaultService.shortDescription,
    sortOrder: Number(safeData.sortOrder ?? defaultService.sortOrder),
    heroLabel: safeData.heroLabel ?? defaultService.heroLabel,
    heroCtaText: safeData.heroCtaText ?? defaultService.heroCtaText,
    processLabel: safeData.processLabel ?? defaultService.processLabel,
    toolsLabel: safeData.toolsLabel ?? defaultService.toolsLabel,
    mobileAppsLabel: safeData.mobileAppsLabel ?? defaultService.mobileAppsLabel,
    portfolioLabel: safeData.portfolioLabel ?? defaultService.portfolioLabel,
    approachList: Array.isArray(safeData.approachList) ? safeData.approachList : [],
    toolsList: Array.isArray(safeData.toolsList) ? safeData.toolsList : [],
    mobileApps: Array.isArray(safeData.mobileApps) ? safeData.mobileApps : [],
    faqs: Array.isArray(safeData.faqs) ? safeData.faqs : []
  };
};

const ServiceForm = ({ initialData, onSubmit, onCancel, isSaving }) => {
  const [formData, setFormData] = useState(() => buildFormState(initialData));
  const [activeTab, setActiveTab] = useState('basic');
  const confirmationMessage = 'You have unsaved service changes. Leave this form?';
  const submitLabel = 'Save Service';

  useConfirmNavigation(!isSaving, confirmationMessage);

  useEffect(() => {
    const nextForm = buildFormState(initialData);
    setFormData(nextForm);
  }, [initialData]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const sanitize = (value) => (typeof value === 'string' ? value.trim() : value || '');

    const payload = {
      name: sanitize(formData.name),
      alias: sanitize(formData.alias),
      shortDescription: formData.shortDescription || '',
      sortOrder: Number(formData.sortOrder) || 0,
      heroLabel: formData.heroLabel || '',
      heroTitle: sanitize(formData.heroTitle),
      heroDescription: formData.heroDescription || '',
      heroCtaText: formData.heroCtaText || '',
      heroMainImage: formData.heroMainImage || '',
      approachImage: formData.approachImage || '',
      processLabel: formData.processLabel || '',
      processTitle: formData.processTitle || '',
      toolsLabel: formData.toolsLabel || '',
      toolsTitle: formData.toolsTitle || '',
      mobileAppsLabel: formData.mobileAppsLabel || '',
      mobileAppsTitle: formData.mobileAppsTitle || '',
      portfolioLabel: formData.portfolioLabel || '',
      portfolioTitle: formData.portfolioTitle || '',
      approachList: (formData.approachList || []).map((item) => ({
        title: item?.title?.trim() || '',
        description: item?.description?.trim() || ''
      })),
      toolsList: (formData.toolsList || []).map((item) => ({
        name: item?.name?.trim() || '',
        imageUrl: item?.imageUrl || ''
      })),
      mobileApps: (formData.mobileApps || []).map((item) => ({
        title: item?.title?.trim() || '',
        description: item?.description?.trim() || '',
        image: item?.image || ''
      })),
      faqs: (formData.faqs || [])
        .map((item, index) => ({
          question: sanitize(item?.question),
          answer: sanitize(item?.answer),
          sortOrder: Number(item?.sortOrder ?? index) || index
        }))
        .filter((item) => item.question && item.answer)
        .sort((a, b) => a.sortOrder - b.sortOrder),
      service_icon: formData.service_icon || ''
    };

    onSubmit(payload);
  };

  const handleCancelClick = () => {
    if (!window.confirm(confirmationMessage)) {
      return;
    }
    onCancel();
  };

  const heroFields = useMemo(
    () => [
      { label: 'Hero Label', name: 'heroLabel' },
      { label: 'Hero Title', name: 'heroTitle' },
      { label: 'Hero Description', name: 'heroDescription', type: 'textarea' },
      { label: 'CTA Text', name: 'heroCtaText' },
      { label: 'Hero Image', name: 'heroMainImage', type: 'image', helperText: 'Recommended 1200x800px JPG/PNG' }
    ],
    []
  );

  return (
    <form className="service-form" onSubmit={handleSubmit}>
      <FormActions sticky isSaving={isSaving} onCancel={handleCancelClick} submitLabel={submitLabel} />

      <div className="form-tabs" role="tablist">
        {[
          { key: 'basic', label: 'Basic Details' },
          { key: 'hero', label: 'Hero' },
          { key: 'process', label: 'Process' },
          { key: 'tools', label: 'Tools' },
          { key: 'mobile', label: 'Mobile Apps' },
          { key: 'faqs', label: 'FAQs' }
        ].map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`tab-btn ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
            role="tab"
            aria-selected={activeTab === tab.key}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <section className="form-grid">
        {activeTab === 'basic' && (
          <div className="form-card">
            <h2>Basic Details</h2>
            <div className="form-group">
              <label htmlFor="name">Service Name *</label>
              <input id="name" name="name" value={formData.name} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label htmlFor="alias">Alias (URL slug) *</label>
              <input
                id="alias"
                name="alias"
                value={formData.alias}
                onChange={handleChange}
                placeholder="app-development"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="shortDescription">Short Description</label>
              <textarea
                id="shortDescription"
                name="shortDescription"
                value={formData.shortDescription}
                onChange={handleChange}
                rows="3"
              />
              <p className="field-hint">Used on the homepage services cards.</p>
            </div>
            <div className="form-group">
              <label htmlFor="service_icon">Service Icon (URL or upload)</label>
              <ImageUploader
                value={formData.service_icon}
                onChange={(url) => setFormData((prev) => ({ ...prev, service_icon: url }))}
                helperText="Shown on service cards across the site."
              />
            </div>
            <div className="form-group">
              <label htmlFor="sortOrder">Display Order</label>
              <input
                id="sortOrder"
                name="sortOrder"
                type="number"
                min="0"
                value={formData.sortOrder}
                onChange={handleChange}
              />
              <p className="field-hint">Lower numbers appear first across the site.</p>
            </div>
          </div>
        )}

        {activeTab === 'hero' && (
          <div className="form-card">
            <h2>Hero Section</h2>
            {heroFields.map((field) => (
              <div className="form-group" key={field.name}>
                <label htmlFor={field.name}>{field.label}</label>
                {field.type === 'textarea' ? (
                  <textarea id={field.name} name={field.name} value={formData[field.name]} onChange={handleChange} />
                ) : field.type === 'image' ? (
                  <ImageUploader
                    value={formData[field.name]}
                    onChange={(url) => setFormData((prev) => ({ ...prev, [field.name]: url }))}
                    helperText={field.helperText}
                  />
                ) : (
                  <input id={field.name} name={field.name} value={formData[field.name]} onChange={handleChange} />
                )}
              </div>
            ))}
          </div>
        )}

        {activeTab === 'process' && (
          <div className="form-card">
            <h2>Process Section</h2>
            <div className="form-group">
              <label htmlFor="processLabel">Process Label</label>
              <input id="processLabel" name="processLabel" value={formData.processLabel} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label htmlFor="processTitle">Process Title</label>
              <input id="processTitle" name="processTitle" value={formData.processTitle} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label htmlFor="approachImage">Approach Image</label>
              <ImageUploader
                value={formData.approachImage}
                onChange={(url) => setFormData((prev) => ({ ...prev, approachImage: url }))}
                helperText="Shown next to process overview"
              />
            </div>

            <ListEditor
              label="Approach Steps"
              items={formData.approachList}
              onChange={(items) => setFormData((prev) => ({ ...prev, approachList: items }))}
              emptyItem={{ title: '', description: '' }}
              fields={[
                { name: 'title', label: 'Title' },
                { name: 'description', label: 'Description', type: 'textarea' }
              ]}
            />
          </div>
        )}

        {activeTab === 'tools' && (
          <div className="form-card">
            <h2>Tools Section</h2>
            <div className="form-group">
              <label htmlFor="toolsLabel">Tools Label</label>
              <input id="toolsLabel" name="toolsLabel" value={formData.toolsLabel} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label htmlFor="toolsTitle">Tools Title</label>
              <input id="toolsTitle" name="toolsTitle" value={formData.toolsTitle} onChange={handleChange} />
            </div>

            <ListEditor
              label="Tools"
              items={formData.toolsList}
              onChange={(items) => setFormData((prev) => ({ ...prev, toolsList: items }))}
              emptyItem={{ name: '', imageUrl: '' }}
              fields={[
                { name: 'name', label: 'Name' },
                { name: 'imageUrl', label: 'Logo', type: 'image', helperText: 'Square logos look best' }
              ]}
            />
          </div>
        )}

        {activeTab === 'mobile' && (
          <div className="form-card">
            <h2>Mobile Apps Section</h2>
            <div className="form-group">
              <label htmlFor="mobileAppsLabel">Section Label</label>
              <input id="mobileAppsLabel" name="mobileAppsLabel" value={formData.mobileAppsLabel} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label htmlFor="mobileAppsTitle">Section Title</label>
              <input id="mobileAppsTitle" name="mobileAppsTitle" value={formData.mobileAppsTitle} onChange={handleChange} />
            </div>

            <ListEditor
              label="Mobile App Types"
              items={formData.mobileApps}
              onChange={(items) => setFormData((prev) => ({ ...prev, mobileApps: items }))}
              emptyItem={{ title: '', description: '', image: '' }}
              fields={[
                { name: 'title', label: 'Title' },
                { name: 'description', label: 'Description', type: 'textarea' },
                { name: 'image', label: 'Image (optional)', type: 'image', helperText: 'Displays above the card' }
              ]}
            />
          </div>
        )}

        {activeTab === 'faqs' && (
          <div className="form-card">
            <h2>FAQs</h2>
            <p className="field-hint">Add common questions to address client concerns in advance.</p>
            <ListEditor
              label="FAQ Items"
              items={formData.faqs}
              onChange={(items) => setFormData((prev) => ({ ...prev, faqs: items }))}
              emptyItem={{ question: '', answer: '', sortOrder: (formData.faqs?.length || 0) + 1 }}
              fields={[
                { name: 'question', label: 'Question', placeholder: 'What is the delivery timeline?' },
                {
                  name: 'answer',
                  label: 'Answer',
                  type: 'textarea',
                  rows: 4,
                  placeholder: 'Share the concise response.'
                },
                {
                  name: 'sortOrder',
                  label: 'Display Order',
                  type: 'number',
                  min: 0,
                  helperText: 'Lower numbers appear first in the list.'
                }
              ]}
            />
          </div>
        )}
      </section>

      <FormActions variant="footer" isSaving={isSaving} onCancel={handleCancelClick} submitLabel={submitLabel} />
    </form>
  );
};

export default ServiceForm;
